import { useMemo } from "react";
import { Bell, Info, MessageCircle, SlidersHorizontal, WalletCards } from "lucide-react";
import { menu, IconeMenuPlataforma, type ItemMenu, type IconeComponente } from "./navegacao";
import { IconeChatGPT } from "../components/icons";
import {
  lerConfiguracoesSistemaPublicas,
  rotaLiberadaNasConfiguracoes,
  useConfiguracoesSistemaPublicas,
  type ConfiguracoesSistemaPublicas,
} from "../utils/configuracoesSistema";
import { obterIconeMenuPorRota } from "./iconesMenu";
import { obterDescricaoModulo, obterDescricaoTelaPorRota } from "./descricoesTelas";

const MODULOS_PERFIL = new Set([
  "Visão Geral",
  "Cadastros",
  "Vendas",
  "Compras",
  // Deve acompanhar o rótulo do menu para registrar suas telas no Perfil de Acesso.
  "Revenda",
  "Estoque",
  "Financeiro",
  "Tickets",
  "Produção",
  "Área do Cliente",
]);

/* Telas do grupo "Plataforma" no Perfil de Acesso. Não estão na sidebar (o
   acesso é pelo menu do usuário), então a lista é declarada aqui — a ordem é a
   mesma que aparece no cadastro. */
export const ROTAS_RECURSOS_PLATAFORMA = {
  agente: "/plataforma/agente",
  chat: "/plataforma/chat",
  centralNotificacoes: "/plataforma/central-notificacoes",
  preferenciasNotificacoes: "/plataforma/notificacoes",
  minhaConta: "/plataforma/minha-conta",
  ajuda: "/ajuda",
} as const;

const TELAS_PLATAFORMA_PERFIL: Array<{ rota: string; rotulo: string; Icone?: IconeComponente }> = [
  { rota: "/protocolo", rotulo: "Protocolo" },
  { rota: "/bloco-notas", rotulo: "Bloco de Notas" },
  { rota: "/auditoria/log-acesso", rotulo: "Log de Acesso" },
  { rota: ROTAS_RECURSOS_PLATAFORMA.agente, rotulo: "Agente", Icone: IconeChatGPT },
  { rota: ROTAS_RECURSOS_PLATAFORMA.chat, rotulo: "Chat", Icone: MessageCircle },
  { rota: ROTAS_RECURSOS_PLATAFORMA.centralNotificacoes, rotulo: "Central de notificações", Icone: Bell },
  { rota: ROTAS_RECURSOS_PLATAFORMA.preferenciasNotificacoes, rotulo: "Notificações", Icone: SlidersHorizontal },
  { rota: ROTAS_RECURSOS_PLATAFORMA.minhaConta, rotulo: "Minha conta", Icone: WalletCards },
  { rota: ROTAS_RECURSOS_PLATAFORMA.ajuda, rotulo: "Ajuda", Icone: Info },
];

export interface TelaDisponivel {
  chave: string;
  rotulo: string;
  /** Descrição curta exibida nos cards de permissão (vazia quando não cadastrada). */
  descricao: string;
  Icone: IconeComponente;
}

export interface GrupoTelas {
  titulo: string;
  /** Descrição curta do módulo, exibida no cabeçalho do painel de permissões. */
  descricao: string;
  /** Ícone do módulo do sidebar (mesmo ícone do grupo em navegacao.ts), para exibir nos cards de permissão. */
  Icone: IconeComponente;
  telas: TelaDisponivel[];
}

function normalizarChave(rota: string): string {
  const semBarra = rota.replace(/^\//, "");
  return semBarra === "" ? "visao-geral" : semBarra;
}

const ROTULO_RELATORIO_POR_ROTA: Record<string, string> = {
  "/vendas/relatorios": "Relatórios de Vendas",
  "/compras/relatorios": "Relatórios de Compras",
  "/fornecedor/relatorios": "Relatórios do Fornecedor",
  "/estoque/relatorios": "Relatórios de Estoque",
  "/financeiro/relatorios": "Relatórios Financeiros",
  "/suporte/relatorios": "Relatórios de Tickets",
  "/producao/relatorios": "Relatórios de Produção",
  "/area-cliente/relatorios": "Relatórios de clientes",
};

function rotuloTelaNoPerfil(rotulo: string, rota: string): string {
  return ROTULO_RELATORIO_POR_ROTA[rota] ?? rotulo;
}

function moduloLiberado(modulo: ItemMenu["modulo"], modulosAtivos: string[]): boolean {
  if (!modulo) return true;
  const lista = Array.isArray(modulo) ? modulo : [modulo];
  return lista.some((item) => modulosAtivos.includes(item));
}

function rotaDisponivelParaPerfil(rota: string | undefined): boolean {
  return !String(rota || "").startsWith("/configuracoes");
}

function filtrarMenuPorConfiguracoes(
  configuracoes: Pick<ConfiguracoesSistemaPublicas, "modulosAtivos" | "tefMenu" | "fiscalMenu" | "fiscalIntegracao">
) {
  return menu
    .filter((item) => MODULOS_PERFIL.has(item.rotulo))
    .map((item) => {
      if (!item.subitens) {
        return rotaDisponivelParaPerfil(item.rota) && moduloLiberado(item.modulo, configuracoes.modulosAtivos)
          ? item
          : null;
      }

      const subitens = item.subitens.filter((subitem) =>
        rotaDisponivelParaPerfil(subitem.rota) &&
        moduloLiberado(item.modulo, configuracoes.modulosAtivos) &&
        moduloLiberado(subitem.modulo ?? item.modulo, configuracoes.modulosAtivos) &&
        rotaLiberadaNasConfiguracoes(
          subitem.rota,
          configuracoes.tefMenu,
          configuracoes.fiscalMenu,
          configuracoes.fiscalIntegracao
        )
      );

      return subitens.length ? { ...item, subitens } : null;
    })
    .filter((item): item is ItemMenu => item !== null);
}

export function obterGruposTelasDisponiveis(
  configuracoes = lerConfiguracoesSistemaPublicas()
): GrupoTelas[] {
  const grupos = filtrarMenuPorConfiguracoes(configuracoes).map((item) => {
    if (item.subitens) {
      return {
        titulo: item.rotulo,
        descricao: obterDescricaoModulo(item.rotulo),
        Icone: item.Icone,
        telas: item.subitens.map((subitem) => ({
          chave: normalizarChave(subitem.rota),
          rotulo: rotuloTelaNoPerfil(subitem.rotulo, subitem.rota),
          descricao: obterDescricaoTelaPorRota(subitem.rota),
          Icone: obterIconeMenuPorRota(subitem.rota) ?? item.Icone,
        })),
      };
    }

    return {
      titulo: item.rotulo,
      descricao: obterDescricaoModulo(item.rotulo),
      Icone: item.Icone,
      telas: [{
        chave: normalizarChave(item.rota ?? "/"),
        rotulo: item.rotulo,
        descricao: obterDescricaoTelaPorRota(item.rota ?? "/"),
        Icone: obterIconeMenuPorRota(item.rota ?? "/") ?? item.Icone,
      }],
    };
  });

  /* Grupo "Plataforma": telas gerais que não pertencem a um módulo e não ficam
     na sidebar (abrem pelo menu do usuário), mas precisam ser liberadas ou
     bloqueadas por Perfil de Acesso como qualquer outra. */
  const telasPlataforma = TELAS_PLATAFORMA_PERFIL.map((tela) => ({
    chave: normalizarChave(tela.rota),
    rotulo: tela.rotulo,
    descricao: obterDescricaoTelaPorRota(tela.rota),
    Icone: tela.Icone ?? obterIconeMenuPorRota(tela.rota) ?? IconeMenuPlataforma,
  }));

  if (telasPlataforma.length) {
    grupos.push({
      titulo: "Plataforma",
      descricao: obterDescricaoModulo("Plataforma"),
      Icone: IconeMenuPlataforma,
      telas: telasPlataforma,
    });
  }

  return grupos;
}

export function obterTodasChavesTelasDisponiveis(
  configuracoes = lerConfiguracoesSistemaPublicas()
): string[] {
  return obterGruposTelasDisponiveis(configuracoes).flatMap((grupo) =>
    grupo.telas.map((tela) => tela.chave)
  );
}

export function useTelasDisponiveis() {
  const configuracoes = useConfiguracoesSistemaPublicas();

  const gruposTelas = useMemo(
    () => obterGruposTelasDisponiveis(configuracoes),
    [configuracoes]
  );

  const todasChavesTelas = useMemo(
    () => gruposTelas.flatMap((grupo) => grupo.telas.map((tela) => tela.chave)),
    [gruposTelas]
  );

  return { gruposTelas, todasChavesTelas };
}
